import { api } from './axios';

/**
 * Валидация ID
 * @param {string|number} id - Идентификатор
 * @param {string} name - Название поля для ошибки
 */
const validateId = (id, name = 'ID') => {
    if (!id && id !== 0) throw new Error(`${name} is required`);
};

/**
 * Типы чатов согласно API Reference (ChatType enum на сервере)
 */
export const ChatType = {
    DIRECT: 'DIRECT',               // Личный чат между двумя пользователями
    GROUP: 'GROUP',                 // Обычный групповой чат
    BATTLE_GROUP: 'BATTLE_GROUP',   // Групповой чат для баттлов
    DUEL_GROUP: 'DUEL_GROUP',       // Групповой чат для дуэлей
    BATTLE_SUPPORT: 'BATTLE_SUPPORT', // Чат поддержки баттла
    BATTLE_LIVE: 'BATTLE_LIVE',     // Live-чат для зрителей баттла
    BATTLE_PARTICIPANTS: 'BATTLE_PARTICIPANTS' // Приватный чат для участников/судей баттла
};

/**
 * API для работы с чатами
 */
export const chatsApi = {
    /**
     * Получает список чатов текущего пользователя
     * Серверный эндпоинт GET /api/chats не поддерживает параметры фильтрации
     * и пагинации — возвращает полный список чатов пользователя.
     * @param {Object} [params] - Зарезервирован для совместимости (сервер игнорирует)
     * @returns {Promise<Array>} Список чатов (ChatResponse[])
     */
    listChats: async (params = {}) => {
        try {
            const { data } = await api.get('/chats', { params });
            if (Array.isArray(data)) {
                return data;
            }
            if (data && typeof data === 'object' && Array.isArray(data.content)) {
                return data.content;
            }
            return [];
        } catch (error) {
            console.error('[chatsApi.listChats] Failed to fetch chats:', error.message);
            throw error;
        }
    },

    /**
     * Получает информацию о чате
     * @param {string|number} chatId - ID чата
     * @param {AbortSignal} [signal] - Опциональный сигнал для отмены запроса
     * @returns {Promise<Object>} Объект чата
     */
    getChat: async (chatId, signal = null) => {
        validateId(chatId, 'chatId');
        try {
            const { data } = await api.get(`/chats/${chatId}`, { signal });
            return data;
        } catch (error) {
            console.error(`[chatsApi.getChat] Failed to fetch chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает историю сообщений чата с пагинацией
     * GET /api/chats/{chatId}/messages
     * @param {string} chatId - ID чата (UUID)
     * @param {Object} [params] - Параметры пагинации
     * @param {number} [params.page=0] - Номер страницы
     * @param {number} [params.size=50] - Размер страницы
     * @param {AbortSignal} [signal] - Сигнал отмены
     * @returns {Promise<Object|Array>} Page-объект с content/totalElements/totalPages
     *         либо плоский массив (если сервер вернул List)
     */
    getMessages: async (chatId, params = {}, signal = null) => {
        validateId(chatId, 'chatId');
        try {
            const { data } = await api.get(`/chats/${chatId}/messages`, { params, signal });
            return data;
        } catch (error) {
            console.error(`[chatsApi.getMessages] Failed to fetch messages for chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Отправляет сообщение в чат
     * POST /api/chats/{chatId}/messages
     * senderId устанавливается сервером автоматически из аутентификации.
     * chatId в теле игнорируется (берётся из пути), но отправляется для
     * совместимости с серверной десериализацией MessageRequest.
     * @param {string} chatId - ID чата (UUID)
     * @param {Object} payload - Данные сообщения (MessageRequest)
     * @param {string} payload.content - Текст сообщения
     * @param {string[]} [payload.attachmentIds] - ID вложений (UUID)
     * @param {string} [payload.stickerId] - ID стикера (UUID) для type=STICKER
     * @param {string} [payload.type] - Тип сообщения ('STICKER' для стикеров)
     * @returns {Promise<Object>} Созданное сообщение (MessageResponse)
     */
    sendMessage: async (chatId, payload) => {
        validateId(chatId, 'chatId');
        if (!payload) throw new Error('Message payload is required');
        try {
            const body = {
                chatId,
                content: payload.content ?? null,
                attachmentIds: payload.attachmentIds || []
            };
            // replyToMessageId — ответ на сообщение (V2: MessageRequest.replyToMessageId)
            if (payload.replyToMessageId) {
                body.replyToMessageId = payload.replyToMessageId;
            }
            // Стикер: type + stickerId
            if (payload.stickerId) {
                body.type = 'STICKER';
                body.stickerId = payload.stickerId;
            }
            // GIF: отправляем как обычное сообщение с вложением
            const { data } = await api.post(`/chats/${chatId}/messages`, body);
            return data;
        } catch (error) {
            console.error(`[chatsApi.sendMessage] Failed to send message in chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Создаёт новый чат
     * POST /api/chats/create
     * @param {Object} payload - Данные чата (ChatRequest)
     * @param {string} [payload.title] - Название чата (null для DIRECT)
     * @param {string[]} payload.participants - Массив ID участников (UUID профилей)
     * @param {string[]} [payload.admins] - Массив ID администраторов (UUID профилей)
     * @param {string} [payload.chatType=GROUP] - Тип чата: 'DIRECT', 'GROUP', 'BATTLE_SUPPORT'
     * @param {boolean} [payload.isArchived=false] - Архивный ли чат
     * @returns {Promise<Object>} Созданный чат (ChatResponse)
     */
    createChat: async (payload) => {
        if (!payload) throw new Error('Chat payload is required');

        const isDirect = payload.chatType === ChatType.DIRECT;

        // title обязателен только для групповых чатов
        if (!isDirect && !payload.title) throw new Error('title is required for group chats');
        if (!payload.participants || !Array.isArray(payload.participants)) {
            throw new Error('participants array is required');
        }

        // Формат соответствует серверному ChatRequest
        const apiPayload = {
            title: payload.title || null,
            participants: payload.participants,
            admins: payload.admins || [],
            chatType: payload.chatType || ChatType.GROUP,
            isArchived: payload.isArchived ?? false
        };

        try {
            const { data } = await api.post('/chats/create', apiPayload);
            return data;
        } catch (error) {
            console.error('[chatsApi.createChat] Failed to create chat:', error.message);
            throw error;
        }
    },

    /**
     * Удаляет чат
     * @param {string|number} chatId - ID чата
     * @returns {Promise<Object>} Ответ от сервера
     */
    deleteChat: async (chatId) => {
        validateId(chatId, 'chatId');
        try {
            const { data } = await api.delete(`/chats/${chatId}`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.deleteChat] Failed to delete chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает участников чата
     * @param {string|number} chatId - ID чата
     * @returns {Promise<string[]>} Массив ID участников
     */
    getChatParticipants: async (chatId) => {
        validateId(chatId, 'chatId');
        try {
            const { data } = await api.get(`/chats/${chatId}/participants`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.getChatParticipants] Failed to fetch participants for chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Добавляет участника в чат
     * POST /api/chats/{chatId}/participants?profileId={profileId}
     * @param {string} chatId - ID чата (UUID)
     * @param {string} profileId - ID профиля для добавления (UUID)
     * @returns {Promise<Object>} Обновлённый чат (ChatResponse)
     */
    addParticipant: async (chatId, profileId) => {
        validateId(chatId, 'chatId');
        if (!profileId) throw new Error('profileId is required');
        try {
            const { data } = await api.post(`/chats/${chatId}/participants`, null, {
                params: { profileId }
            });
            return data;
        } catch (error) {
            console.error(`[chatsApi.addParticipant] Failed to add participant to chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Покидает чат
     * @param {string|number} chatId - ID чата
     * @returns {Promise<Object>}
     */
    leaveChat: async (chatId) => {
        validateId(chatId, 'chatId');
        try {
            const { data } = await api.post(`/chats/${chatId}/leave`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.leaveChat] Failed to leave chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Закрепить/открепить чат
     * POST /api/chats/{chatId}/toggle-pin
     * @param {string} chatId - ID чата (UUID)
     * @returns {Promise<Object>} Обновлённый чат (ChatResponse) с новым isPinned
     */
    togglePin: async (chatId) => {
        validateId(chatId, 'chatId');
        try {
            const { data } = await api.post(`/chats/${chatId}/toggle-pin`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.togglePin] Failed to toggle pin for chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Архивировать/разархивировать чат
     * POST /api/chats/{chatId}/toggle-archive
     * @param {string} chatId - ID чата (UUID)
     * @returns {Promise<Object>} Обновлённый чат (ChatResponse) с новым isArchived
     */
    toggleArchive: async (chatId) => {
        validateId(chatId, 'chatId');
        try {
            const { data } = await api.post(`/chats/${chatId}/toggle-archive`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.toggleArchive] Failed to toggle archive for chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Добавляет участника в чат баттла (BATTLE_PARTICIPANTS)
     * @param {string|number} battleId - ID баттла
     * @param {string} profileId - ID профиля участника
     * @param {string} [role] - Роль участника: 'OPPONENT', 'REFEREE', 'VIEWER'
     * @returns {Promise<Object>} Обновлённый чат с участниками
     */
    addBattleParticipant: async (battleId, profileId, role = null) => {
        validateId(battleId, 'battleId');
        if (!profileId) throw new Error('profileId is required');
        try {
            const params = { profileId };
            if (role) params.role = role;
            const { data } = await api.post(`/battles/${battleId}/chats/participants/add`, {}, { params });
            return data;
        } catch (error) {
            console.error(`[chatsApi.addBattleParticipant] Failed to add participant to battle ${battleId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удаляет участника из чата баттла
     * @param {string|number} battleId - ID баттла
     * @param {string} profileId - ID профиля участника
     * @returns {Promise<Object>} Обновлённый чат с участниками
     */
    removeBattleParticipant: async (battleId, profileId) => {
        validateId(battleId, 'battleId');
        if (!profileId) throw new Error('profileId is required');
        try {
            const { data } = await api.post(`/battles/${battleId}/chats/participants/remove`, {}, { params: { profileId } });
            return data;
        } catch (error) {
            console.error(`[chatsApi.removeBattleParticipant] Failed to remove participant from battle ${battleId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает все чаты баттла
     * @param {string|number} battleId - ID баттла
     * @returns {Promise<Array>} Список чатов баттла
     */
    getBattleChats: async (battleId) => {
        validateId(battleId, 'battleId');
        try {
            const { data } = await api.get(`/battles/${battleId}/chats`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.getBattleChats] Failed to fetch chats for battle ${battleId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает чат для зрителей (live chat) баттла
     * @param {string|number} battleId - ID баттла
     * @returns {Promise<Object>} Чат зрителей
     */
    getBattleLiveChat: async (battleId) => {
        validateId(battleId, 'battleId');
        try {
            const { data } = await api.get(`/battles/${battleId}/chats/live`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.getBattleLiveChat] Failed to fetch live chat for battle ${battleId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает чат участников баттла
     * @param {string|number} battleId - ID баттла
     * @returns {Promise<Object>} Чат участников
     */
    getBattleParticipantsChat: async (battleId) => {
        validateId(battleId, 'battleId');
        try {
            const { data } = await api.get(`/battles/${battleId}/chats/participants`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.getBattleParticipantsChat] Failed to fetch participants chat for battle ${battleId}:`, error.message);
            throw error;
        }
    },

    /**
     * Отмечает сообщения чата как прочитанные
     * POST /api/chats/{chatId}/mark-read
     * @param {string} chatId - ID чата (UUID)
     * @param {string[]} [profileIds=[]] - ID профилей (UUID), для которых отметить прочитанным
     * @returns {Promise<{successful: boolean}>}
     */
    markAsRead: async (chatId, profileIds = []) => {
        validateId(chatId, 'chatId');
        try {
            const { data } = await api.post(`/chats/${chatId}/mark-read`, profileIds);
            return data;
        } catch (error) {
            console.error(`[chatsApi.markAsRead] Failed to mark chat ${chatId} as read:`, error.message);
            throw error;
        }
    },

    /**
     * Отмечает сообщения чата как прочитанные для конкретного профиля
     * POST /api/messages/mark-read/{chatId}/{profileId}
     * @param {string} chatId - ID чата (UUID)
     * @param {string} profileId - ID профиля (UUID)
     * @returns {Promise<{successful: boolean}>}
     */
    markAsReadForProfile: async (chatId, profileId) => {
        validateId(chatId, 'chatId');
        validateId(profileId, 'profileId');
        try {
            const { data } = await api.post(`/messages/mark-read/${chatId}/${profileId}`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.markAsReadForProfile] Failed to mark chat ${chatId} as read for profile ${profileId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить непрочитанные сообщения в конкретном чате для профиля
     * GET /api/chats/{chatId}/unread-for-profile/{profileId}
     * @param {string} chatId - ID чата (UUID)
     * @param {string} profileId - ID профиля (UUID — должен совпадать с ID текущего профиля)
     * @returns {Promise<Array>} Массив непрочитанных сообщений (MessageResponse[])
     */
    getUnreadMessagesInChat: async (chatId, profileId) => {
        validateId(chatId, 'chatId');
        validateId(profileId, 'profileId');
        try {
            const { data } = await api.get(`/chats/${chatId}/unread-for-profile/${profileId}`);
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error(`[chatsApi.getUnreadMessagesInChat] Failed to fetch unread messages for chat ${chatId}, profile ${profileId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удаляет сообщение
     * @param {string|number} messageId - ID сообщения
     * @returns {Promise<void>}
     */
    deleteMessage: async (messageId) => {
        validateId(messageId, 'messageId');
        try {
            await api.delete(`/chats/messages/${messageId}`);
        } catch (error) {
            console.error(`[chatsApi.deleteMessage] Failed to delete message ${messageId}:`, error.message);
            throw error;
        }
    },

    /**
     * Редактирует текст сообщения (мягкое редактирование)
     * PATCH /api/chats/messages/{messageId}
     * Только отправитель может редактировать. Удалённые сообщения редактировать нельзя.
     * @param {string} messageId - ID сообщения (UUID)
     * @param {string} content - Новый текст сообщения
     * @returns {Promise<Object>} Обновлённое сообщение (MessageResponse)
     */
    editMessage: async (messageId, content) => {
        validateId(messageId, 'messageId');
        if (content === undefined || content === null) throw new Error('content is required');
        try {
            const { data } = await api.patch(`/chats/messages/${messageId}`, { content });
            return data;
        } catch (error) {
            console.error(`[chatsApi.editMessage] Failed to edit message ${messageId}:`, error.message);
            throw error;
        }
    },

    /**
     * Добавить реакцию (эмодзи) на сообщение
     * POST /api/messages/{messageId}/reactions
     * @param {string} messageId - ID сообщения (UUID)
     * @param {string} emoji - Эмодзи реакции
     * @returns {Promise<Object>} Созданная реакция (MessageReactionResponse)
     */
    addReaction: async (messageId, emoji) => {
        validateId(messageId, 'messageId');
        if (!emoji) throw new Error('emoji is required');
        try {
            const { data } = await api.post(`/messages/${messageId}/reactions`, { emoji });
            return data;
        } catch (error) {
            console.error(`[chatsApi.addReaction] Failed to add reaction to message ${messageId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить реакцию (эмодзи) текущего пользователя с сообщения
     * DELETE /api/messages/{messageId}/reactions?emoji=...
     * @param {string} messageId - ID сообщения (UUID)
     * @param {string} emoji - Эмодзи для удаления
     * @returns {Promise<void>}
     */
    removeReaction: async (messageId, emoji) => {
        validateId(messageId, 'messageId');
        if (!emoji) throw new Error('emoji is required');
        try {
            await api.delete(`/messages/${messageId}/reactions`, { params: { emoji } });
        } catch (error) {
            console.error(`[chatsApi.removeReaction] Failed to remove reaction from message ${messageId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить все реакции для сообщения
     * GET /api/messages/{messageId}/reactions
     * @param {string} messageId - ID сообщения (UUID)
     * @returns {Promise<Array>} Массив реакций (MessageReactionResponse[])
     */
    getMessageReactions: async (messageId) => {
        validateId(messageId, 'messageId');
        try {
            const { data } = await api.get(`/messages/${messageId}/reactions`);
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error(`[chatsApi.getMessageReactions] Failed to fetch reactions for message ${messageId}:`, error.message);
            throw error;
        }
    },

    /**
     * Назначить администратора чата
     * POST /api/chats/{chatId}/admins/{profileId}
     * Только администраторы могут назначать других админов.
     * @param {string} chatId - ID чата (UUID)
     * @param {string} profileId - ID профиля (UUID)
     * @returns {Promise<Object>} Обновлённый чат (ChatResponse)
     */
    assignAdmin: async (chatId, profileId) => {
        validateId(chatId, 'chatId');
        validateId(profileId, 'profileId');
        try {
            const { data } = await api.post(`/chats/${chatId}/admins/${profileId}`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.assignAdmin] Failed to assign admin in chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Снять администратора чата
     * DELETE /api/chats/{chatId}/admins/{profileId}
     * Только администраторы могут снимать других админов.
     * Нельзя снять последнего администратора.
     * @param {string} chatId - ID чата (UUID)
     * @param {string} profileId - ID профиля (UUID)
     * @returns {Promise<Object>} Обновлённый чат (ChatResponse)
     */
    removeAdmin: async (chatId, profileId) => {
        validateId(chatId, 'chatId');
        validateId(profileId, 'profileId');
        try {
            const { data } = await api.delete(`/chats/${chatId}/admins/${profileId}`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.removeAdmin] Failed to remove admin in chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить участника из чата
     * DELETE /api/chats/{chatId}/participants/{profileId}
     * Только администраторы могут удалять участников.
     * @param {string} chatId - ID чата (UUID)
     * @param {string} profileId - ID профиля (UUID)
     * @returns {Promise<Object>} Обновлённый чат (ChatResponse)
     */
    removeParticipant: async (chatId, profileId) => {
        validateId(chatId, 'chatId');
        validateId(profileId, 'profileId');
        try {
            const { data } = await api.delete(`/chats/${chatId}/participants/${profileId}`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.removeParticipant] Failed to remove participant from chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Отправить личное сообщение пользователю (создаёт DIRECT-чат, если не существует)
     * POST /api/messages/send-to-user
     * @param {Object} payload - Данные сообщения (DirectMessageRequest)
     * @param {string} payload.recipientId - ID получателя (UUID профиля)
     * @param {string} payload.content - Текст сообщения
     * @param {string[]} [payload.attachmentIds] - ID вложений (UUID)
     * @returns {Promise<Object>} Созданное сообщение (MessageResponse)
     */
    sendMessageToUser: async (payload) => {
        if (!payload) throw new Error('Message payload is required');
        if (!payload.recipientId) throw new Error('recipientId is required');
        try {
            const body = {
                recipientUserId: payload.recipientId,
                content: payload.content ?? null,
                attachmentIds: payload.attachmentIds || []
            };
            const { data } = await api.post('/messages/send-to-user', body);
            return data;
        } catch (error) {
            console.error('[chatsApi.sendMessageToUser] Failed to send message to user:', error.message);
            throw error;
        }
    },

    /**
     * Получить сообщение по ID
     * @param {string|number} messageId - ID сообщения
     * @returns {Promise<Object>} Сообщение
     */
    getMessage: async (messageId) => {
        validateId(messageId, 'messageId');
        try {
            const { data } = await api.get(`/messages/${messageId}`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.getMessage] Failed to fetch message ${messageId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить непрочитанные сообщения для профиля
     * GET /api/messages/unread/profile/{profileId}
     * @param {string} profileId - ID профиля (UUID)
     * @returns {Promise<Array>} Массив непрочитанных сообщений (MessageResponse[])
     */
    getUnreadMessagesByProfile: async (profileId) => {
        validateId(profileId, 'profileId');
        try {
            const { data } = await api.get(`/messages/unread/profile/${profileId}`);
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error(`[chatsApi.getUnreadMessagesByProfile] Failed to fetch unread messages for profile ${profileId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить количество непрочитанных сообщений для профиля
     * GET /api/messages/unread/profile/{profileId}/count
     * @param {string} profileId - ID профиля (UUID)
     * @returns {Promise<number>} Количество непрочитанных сообщений
     */
    getUnreadCountByProfile: async (profileId) => {
        validateId(profileId, 'profileId');
        try {
            const { data } = await api.get(`/messages/unread/profile/${profileId}/count`);
            return typeof data?.count === 'number' ? data.count : (typeof data === 'number' ? data : 0);
        } catch (error) {
            console.error(`[chatsApi.getUnreadCountByProfile] Failed to fetch unread count for profile ${profileId}:`, error.message);
            throw error;
        }
    },

    /**
     * Отправить сообщение через универсальный эндпоинт
     * POST /api/messages/send
     * Согласно документации senderId устанавливается автоматически из аутентификации.
     * Пока сервер требует senderId в теле — передаём, если указан.
     * @param {Object} payload - Данные сообщения (MessageRequest)
     * @param {string} payload.chatId - ID чата (UUID)
     * @param {string} [payload.senderId] - ID отправителя (UUID, опционально)
     * @param {string} payload.content - Текст сообщения
     * @param {string[]} [payload.attachmentIds] - ID вложений (UUID)
     * @returns {Promise<Object>} Созданное сообщение (MessageResponse)
     */
    sendMessageAlt: async (payload) => {
        if (!payload) throw new Error('Message payload is required');
        if (!payload.chatId) throw new Error('chatId is required');
        try {
            const body = {
                chatId: payload.chatId,
                content: payload.content ?? null,
                attachmentIds: payload.attachmentIds || []
            };
            if (payload.senderId) {
                body.senderId = payload.senderId;
            }
            // replyToMessageId — ответ на сообщение (V2: MessageRequest.replyToMessageId)
            if (payload.replyToMessageId) {
                body.replyToMessageId = payload.replyToMessageId;
            }
            const { data } = await api.post('/messages/send', body);
            return data;
        } catch (error) {
            console.error('[chatsApi.sendMessageAlt] Failed to send message:', error.message);
            throw error;
        }
    },

    /**
     * Обновить название чата (только для администраторов, только GROUP/BATTLE_GROUP)
     * PATCH /api/chats/{chatId}
     * @param {string} chatId - ID чата (UUID)
     * @param {string} title - Новое название чата
     * @returns {Promise<Object>} Обновлённый чат (ChatResponse)
     */
    updateChatTitle: async (chatId, title) => {
        validateId(chatId, 'chatId');
        if (!title || !title.trim()) throw new Error('title is required');
        try {
            const { data } = await api.patch(`/chats/${chatId}`, { title: title.trim() });
            return data;
        } catch (error) {
            console.error(`[chatsApi.updateChatTitle] Failed to update title for chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Очистить историю сообщений чата (только для администраторов)
     * POST /api/chats/{chatId}/clear-history
     * Удаляет все сообщения, но сохраняет сам чат.
     * @param {string} chatId - ID чата (UUID)
     * @returns {Promise<Object>} Обновлённый чат (ChatResponse)
     */
    clearChatHistory: async (chatId) => {
        validateId(chatId, 'chatId');
        try {
            const { data } = await api.delete(`/chats/${chatId}/history`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.clearChatHistory] Failed to clear history for chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Глобальный поиск сообщений по всем чатам текущего пользователя
     * GET /api/chats/messages/search?q=...&limit=...
     * Сервер расшифровывает сообщения перед поиском (AES-256-GCM).
     *
     * Поддерживаемые параметры фильтрации:
     * - senderId — UUID отправителя (только сообщения от этого профиля)
     * - dateFrom — ISO-8601 дата/время начала диапазона
     * - dateTo — ISO-8601 дата/время конца диапазона
     * - hasAttachments — true/false (только сообщения с вложениями)
     *
     * @param {string} query - Поисковый запрос
     * @param {Object} [filters] - Дополнительные фильтры
     * @param {string} [filters.senderId] - ID отправителя (UUID профиля)
     * @param {string} [filters.dateFrom] - Дата начала (ISO-8601)
     * @param {string} [filters.dateTo] - Дата окончания (ISO-8601)
     * @param {boolean} [filters.hasAttachments] - Только с вложениями
     * @param {number} [limit=50] - Максимальное количество результатов
     * @returns {Promise<Array>} Массив найденных сообщений (MessageResponse[])
     */
    searchMessages: async (query, filters = {}, limit = 50) => {
        if (!query || !query.trim()) return [];
        try {
            const params = { q: query.trim(), limit };
            if (filters.senderId) params.senderId = filters.senderId;
            if (filters.dateFrom) params.dateFrom = filters.dateFrom;
            if (filters.dateTo) params.dateTo = filters.dateTo;
            if (filters.hasAttachments != null) params.hasAttachments = filters.hasAttachments;
            const { data } = await api.get('/chats/messages/search', { params });
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error('[chatsApi.searchMessages] Failed to search messages:', error.message);
            throw error;
        }
    },

    /**
     * Закрепить сообщение в чате
     * POST /api/chats/{chatId}/messages/{messageId}/pin
     * @param {string} chatId - ID чата (UUID)
     * @param {string} messageId - ID сообщения (UUID)
     * @returns {Promise<Object>} Обновлённое сообщение
     */
    pinMessage: async (chatId, messageId) => {
        validateId(chatId, 'chatId');
        validateId(messageId, 'messageId');
        try {
            const { data } = await api.post(`/chats/${chatId}/messages/${messageId}/pin`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.pinMessage] Failed to pin message ${messageId} in chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Открепить сообщение в чате
     * DELETE /api/chats/{chatId}/messages/{messageId}/pin
     * @param {string} chatId - ID чата (UUID)
     * @param {string} messageId - ID сообщения (UUID)
     * @returns {Promise<Object>} Обновлённое сообщение
     */
    unpinMessage: async (chatId, messageId) => {
        validateId(chatId, 'chatId');
        validateId(messageId, 'messageId');
        try {
            const { data } = await api.delete(`/chats/${chatId}/messages/${messageId}/pin`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.unpinMessage] Failed to unpin message ${messageId} in chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить список закреплённых сообщений чата
     * GET /api/chats/{chatId}/pinned-messages
     * @param {string} chatId - ID чата (UUID)
     * @returns {Promise<Array>} Массив закреплённых сообщений
     */
    getPinnedMessages: async (chatId) => {
        validateId(chatId, 'chatId');
        try {
            const { data } = await api.get(`/chats/${chatId}/pinned-messages`);
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error(`[chatsApi.getPinnedMessages] Failed to fetch pinned messages for chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Переслать сообщение в другой чат.
     * POST /api/messages/forward
     * Сервер копирует контент и вложения из оригинального сообщения,
     * создаёт новое сообщение с forwardedFrom* полями.
     *
     * @param {string} messageId - ID оригинального сообщения (UUID)
     * @param {string} targetChatId - ID целевого чата (UUID)
     * @param {string} [comment] - Необязательный комментарий к пересланному сообщению
     * @returns {Promise<Object>} Созданное сообщение (MessageResponse)
     */
    forwardMessage: async (messageId, targetChatId, comment = null) => {
        validateId(messageId, 'messageId');
        validateId(targetChatId, 'targetChatId');
        try {
            const body = { messageId, targetChatId };
            if (comment && comment.trim()) {
                body.comment = comment.trim();
            }
            const { data } = await api.post('/messages/forward', body);
            return data;
        } catch (error) {
            console.error(`[chatsApi.forwardMessage] Failed to forward message ${messageId} to chat ${targetChatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Включить mute (отключить уведомления) для чата.
     * PATCH /api/chats/{chatId}/mute
     * Сервер исключает пользователя из получателей NewMessageEvent,
     * поэтому NotificationEventListener не создаёт push-уведомлений.
     *
     * @param {string} chatId - ID чата (UUID)
     * @param {string|null} [mutedUntil=null] - ISO-8601 дата окончания mute
     *   (например "2026-08-21T00:00:00"). null/undefined — бессрочно.
     * @returns {Promise<Object>} Обновлённый чат (ChatResponse) с mutedUntil
     */
    muteChat: async (chatId, mutedUntil = null) => {
        validateId(chatId, 'chatId');
        try {
            const body = mutedUntil ? { mutedUntil } : {};
            const { data } = await api.patch(`/chats/${chatId}/mute`, body);
            return data;
        } catch (error) {
            console.error(`[chatsApi.muteChat] Failed to mute chat ${chatId}:`, error.message);
            throw error;
        }
    },

    /**
     * Выключить mute (включить уведомления) для чата.
     * PATCH /api/chats/{chatId}/unmute
     * @param {string} chatId - ID чата (UUID)
     * @returns {Promise<Object>} Обновлённый чат (ChatResponse)
     */
    unmuteChat: async (chatId) => {
        validateId(chatId, 'chatId');
        try {
            const { data } = await api.patch(`/chats/${chatId}/unmute`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.unmuteChat] Failed to unmute chat ${chatId}:`, error.message);
            throw error;
        }
    },

    // ═══════════════════════════════════════════
    //  Закладки (Bookmarks)
    // ═══════════════════════════════════════════

    /**
     * Добавить закладку на сообщение.
     * POST /api/chats/{chatId}/messages/{messageId}/bookmark
     * Idempotent — повторный вызов возвращает существующую закладку.
     * @param {string} chatId - ID чата
     * @param {string} messageId - ID сообщения
     * @returns {Promise<Object>} MessageBookmarkResponse
     */
    addBookmark: async (chatId, messageId) => {
        validateId(chatId, 'chatId');
        validateId(messageId, 'messageId');
        try {
            const { data } = await api.post(`/chats/${chatId}/messages/${messageId}/bookmark`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.addBookmark] Failed to bookmark message ${messageId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить закладку с сообщения.
     * DELETE /api/chats/{chatId}/messages/{messageId}/bookmark
     * @param {string} chatId - ID чата
     * @param {string} messageId - ID сообщения
     * @returns {Promise<void>}
     */
    removeBookmark: async (chatId, messageId) => {
        validateId(chatId, 'chatId');
        validateId(messageId, 'messageId');
        try {
            await api.delete(`/chats/${chatId}/messages/${messageId}/bookmark`);
        } catch (error) {
            console.error(`[chatsApi.removeBookmark] Failed to remove bookmark from message ${messageId}:`, error.message);
            throw error;
        }
    },

    /**
     * Проверить, есть ли закладка на сообщение у текущего пользователя.
     * GET /api/chats/{chatId}/messages/{messageId}/bookmark
     * @param {string} chatId - ID чата
     * @param {string} messageId - ID сообщения
     * @returns {Promise<{bookmarked: boolean}>}
     */
    checkBookmark: async (chatId, messageId) => {
        validateId(chatId, 'chatId');
        validateId(messageId, 'messageId');
        try {
            const { data } = await api.get(`/chats/${chatId}/messages/${messageId}/bookmark`);
            return data;
        } catch (error) {
            console.error(`[chatsApi.checkBookmark] Failed to check bookmark for message ${messageId}:`, error.message);
            throw error;
        }
    },

    /**
     * Все закладки текущего пользователя.
     * GET /api/users/me/bookmarks?page=0&size=50
     * @param {Object} [params] - { page, size }
     * @returns {Promise<{content: Array, totalElements: number, totalPages: number}>}
     */
    getUserBookmarks: async (params = {}) => {
        try {
            const { data } = await api.get('/users/me/bookmarks', {
                params: { page: params.page ?? 0, size: params.size ?? 50 }
            });
            return data;
        } catch (error) {
            console.error('[chatsApi.getUserBookmarks] Failed to fetch bookmarks:', error.message);
            throw error;
        }
    }
};